import React from 'react';
import { MapPin, Mail, Phone, Clock, Instagram, Facebook, MessageCircle } from 'lucide-react';
import { contactInfo, workingHours } from '../data/mock';
import { Button } from './ui/button';

const Contact = () => {
  const whatsappLink = `https://wa.me/${contactInfo.whatsapp}?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20os%20servi%C3%A7os%20da%20Shape%20%26%20Shine.`;

  return (
    <section id="contactos" className="py-24 bg-[#0a0a0a] relative">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(229,177,196,0.05)_0%,_transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#E5B1C4] text-sm tracking-[0.3em] uppercase mb-4 block">
            Fale Connosco
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-rose-gradient mb-6">
            Contactos
          </h2>
          <div className="flex items-center justify-center">
            <div className="w-24 h-px bg-gradient-to-r from-transparent to-[#E5B1C4]" />
            <span className="mx-4 text-[#E5B1C4]">✦</span>
            <div className="w-24 h-px bg-gradient-to-l from-transparent to-[#E5B1C4]" />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            {/* Address */}
            <div className="flex items-start group">
              <div className="w-14 h-14 rounded-full border border-[#E5B1C4]/30 flex items-center justify-center
                            mr-5 group-hover:border-[#E5B1C4] group-hover:bg-[#E5B1C4]/10 transition-all flex-shrink-0">
                <MapPin className="text-[#E5B1C4]" size={22} />
              </div>
              <div>
                <h4 className="font-display text-xl text-[#f5f0e8] mb-2">Morada</h4>
                <p className="text-[#bbb5ae]">
                  {contactInfo.address}<br />
                  {contactInfo.postalCode}
                </p>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(contactInfo.address + ' ' + contactInfo.postalCode)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E5B1C4] text-sm hover:underline mt-2 inline-block"
                >
                  Ver no Google Maps →
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start group">
              <div className="w-14 h-14 rounded-full border border-[#E5B1C4]/30 flex items-center justify-center
                            mr-5 group-hover:border-[#E5B1C4] group-hover:bg-[#E5B1C4]/10 transition-all flex-shrink-0">
                <Mail className="text-[#E5B1C4]" size={22} />
              </div>
              <div>
                <h4 className="font-display text-xl text-[#f5f0e8] mb-2">Email</h4>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-[#bbb5ae] hover:text-[#E5B1C4] transition-colors"
                >
                  {contactInfo.email}
                </a>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start group">
              <div className="w-14 h-14 rounded-full border border-[#E5B1C4]/30 flex items-center justify-center
                            mr-5 group-hover:border-[#E5B1C4] group-hover:bg-[#E5B1C4]/10 transition-all flex-shrink-0">
                <Phone className="text-[#E5B1C4]" size={22} />
              </div>
              <div>
                <h4 className="font-display text-xl text-[#f5f0e8] mb-2">Telefone</h4>
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="text-[#bbb5ae] hover:text-[#E5B1C4] transition-colors"
                >
                  {contactInfo.phone}
                </a>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <div className="pt-4">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button className="w-full sm:w-auto bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-4 rounded-none font-medium">
                  <MessageCircle size={20} className="mr-3" />
                  Contactar via WhatsApp
                </Button>
              </a>
            </div>

            {/* Social Media */}
            <div className="pt-4">
              <h4 className="font-display text-xl text-[#f5f0e8] mb-4">Siga-nos</h4>
              <div className="flex space-x-4">
                <a
                  href={contactInfo.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full border border-[#E5B1C4]/30 flex items-center justify-center
                           hover:border-[#E5B1C4] hover:bg-[#E5B1C4]/10 transition-all"
                >
                  <Instagram className="text-[#E5B1C4]" size={20} />
                </a>
                <a
                  href={contactInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full border border-[#E5B1C4]/30 flex items-center justify-center
                           hover:border-[#E5B1C4] hover:bg-[#E5B1C4]/10 transition-all"
                >
                  <Facebook className="text-[#E5B1C4]" size={20} />
                </a>
              </div>
            </div>
          </div>

          {/* Working Hours */}
          <div className="bg-[#111111] border border-[#C1A54F]/10 rounded-sm p-8">
            <div className="flex items-center mb-8">
              <div className="w-14 h-14 rounded-full border border-[#C1A54F]/30 flex items-center justify-center mr-5">
                <Clock className="text-[#E1C57C]" size={22} />
              </div>
              <h3 className="font-display text-2xl text-[#f5f0e8]">Horário de Funcionamento</h3>
            </div>
            <div className="space-y-4">
              {workingHours.map((item, index) => (
                <div
                  key={index}
                  className={`flex justify-between py-3 border-b border-[#C1A54F]/10 last:border-0 ${
                    item.hours === 'Encerrado' ? 'opacity-50' : ''
                  }`}
                >
                  <span className="text-[#f5f0e8]">{item.day}</span>
                  <span className={item.hours === 'Encerrado' ? 'text-[#736c64]' : 'text-[#E1C57C]'}>
                    {item.hours}
                  </span>
                </div>
              ))}
            </div>

            {/* Map Embed */}
            <div className="mt-8 rounded-sm overflow-hidden border border-[#C1A54F]/10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3030.8!2d-8.6!3d40.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDM2JzAwLjAiTiA4wrAzNicwMC4wIlc!5e0!3m2!1spt-PT!2spt!4v1234567890"
                width="100%"
                height="200"
                style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização Shape & Shine"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
